import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const JurisdictionTab = ({ contract, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    governingLaw: contract?.governingLaw || 'New York State Law',
    jurisdiction: contract?.jurisdiction || 'New York, NY',
    disputeResolution: contract?.disputeResolution || 'Binding Arbitration',
    venue: contract?.venue || 'New York County Supreme Court',
    arbitrationRules: contract?.arbitrationRules || 'American Arbitration Association Commercial Rules',
    legalNotices: contract?.legalNotices || `Legal notices must be sent to:\n\nTechCorp Solutions:\n123 Technology Drive\nNew York, NY 10001\nAttn: Legal Department\n\nInnovate Services LLC:\n456 Innovation Boulevard\nNew York, NY 10002\nAttn: Contract Manager`,
    complianceRequirements: contract?.complianceRequirements || `This contract must comply with:\n• New York State Business Corporation Law\n• Federal Trade Commission regulations\n• Industry-specific compliance standards\n• Data protection and privacy laws`,
    terminationClauses: contract?.terminationClauses || `Either party may terminate this agreement:\n• With 30 days written notice\n• Immediately for material breach\n• Upon insolvency or bankruptcy\n• As specified in individual work orders`
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    onUpdate(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      governingLaw: contract?.governingLaw || 'New York State Law',
      jurisdiction: contract?.jurisdiction || 'New York, NY',
      disputeResolution: contract?.disputeResolution || 'Binding Arbitration',
      venue: contract?.venue || 'New York County Supreme Court',
      arbitrationRules: contract?.arbitrationRules || 'American Arbitration Association Commercial Rules',
      legalNotices: contract?.legalNotices || `Legal notices must be sent to:\n\nTechCorp Solutions:\n123 Technology Drive\nNew York, NY 10001\nAttn: Legal Department\n\nInnovate Services LLC:\n456 Innovation Boulevard\nNew York, NY 10002\nAttn: Contract Manager`,
      complianceRequirements: contract?.complianceRequirements || `This contract must comply with:\n• New York State Business Corporation Law\n• Federal Trade Commission regulations\n• Industry-specific compliance standards\n• Data protection and privacy laws`,
      terminationClauses: contract?.terminationClauses || `Either party may terminate this agreement:\n• With 30 days written notice\n• Immediately for material breach\n• Upon insolvency or bankruptcy\n• As specified in individual work orders`
    });
    setIsEditing(false);
  };

  const jurisdictionData = {
    state: 'New York',
    country: 'United States',
    coordinates: { lat: 40.7128, lng: -74.0060 },
    timeZone: 'Eastern Time (ET)',
    businessHours: '9:00 AM - 5:00 PM EST',
    legalSystem: 'Common Law',
    currency: 'USD',
    language: 'English'
  };

  return (
    <div className="p-6 bg-background">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Jurisdiction & Legal Framework</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Legal jurisdiction, governing law, and dispute resolution mechanisms
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {isEditing ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Save"
                  iconPosition="left"
                  onClick={handleSave}
                >
                  Save Changes
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                iconName="Edit3"
                iconPosition="left"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Legal Framework */}
          <div className="space-y-6">
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <Icon name="Scale" size={20} className="mr-2 text-primary" />
                Governing Law & Jurisdiction
              </h3>
              
              <div className="space-y-4">
                <Input
                  label="Governing Law"
                  type="text"
                  value={formData?.governingLaw}
                  onChange={(e) => handleInputChange('governingLaw', e?.target?.value)}
                  disabled={!isEditing}
                  description="The legal system that governs this contract"
                />
                
                <Input
                  label="Legal Jurisdiction"
                  type="text"
                  value={formData?.jurisdiction}
                  onChange={(e) => handleInputChange('jurisdiction', e?.target?.value)}
                  disabled={!isEditing}
                  description="Primary jurisdiction for legal proceedings"
                />

                <Input
                  label="Dispute Resolution Method"
                  type="text"
                  value={formData?.disputeResolution}
                  onChange={(e) => handleInputChange('disputeResolution', e?.target?.value)}
                  disabled={!isEditing}
                  description="Method for resolving contract disputes"
                />

                <Input
                  label="Legal Venue"
                  type="text"
                  value={formData?.venue}
                  onChange={(e) => handleInputChange('venue', e?.target?.value)}
                  disabled={!isEditing}
                  description="Specific court or location for legal proceedings"
                />

                <Input
                  label="Arbitration Rules"
                  type="text"
                  value={formData?.arbitrationRules}
                  onChange={(e) => handleInputChange('arbitrationRules', e?.target?.value)}
                  disabled={!isEditing}
                  description="Applicable arbitration rules and procedures"
                />
              </div>
            </div>

            {/* Compliance Requirements */}
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <Icon name="Shield" size={20} className="mr-2 text-primary" />
                Compliance Requirements
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Regulatory Compliance
                </label>
                <textarea
                  value={formData?.complianceRequirements}
                  onChange={(e) => handleInputChange('complianceRequirements', e?.target?.value)}
                  disabled={!isEditing}
                  rows={6}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground disabled:bg-muted disabled:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Right Column - Location & Notices */}
          <div className="space-y-6">
            {/* Jurisdiction Map */}
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <Icon name="MapPin" size={20} className="mr-2 text-primary" />
                Jurisdiction Location
              </h3>
              
              <div className="aspect-video rounded-lg overflow-hidden border border-border mb-4">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="New York Jurisdiction"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${jurisdictionData?.coordinates?.lat},${jurisdictionData?.coordinates?.lng}&z=10&output=embed`}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground mb-1">State/Province</p>
                  <p className="font-medium text-foreground">{jurisdictionData?.state}</p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Country</p>
                  <p className="font-medium text-foreground">{jurisdictionData?.country}</p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Time Zone</p>
                  <p className="font-medium text-foreground">{jurisdictionData?.timeZone}</p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Legal System</p>
                  <p className="font-medium text-foreground">{jurisdictionData?.legalSystem}</p>
                </div>
              </div>
            </div>

            {/* Legal Notices */}
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <Icon name="Mail" size={20} className="mr-2 text-primary" />
                Legal Notices
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Notice Addresses
                </label>
                <textarea
                  value={formData?.legalNotices}
                  onChange={(e) => handleInputChange('legalNotices', e?.target?.value)}
                  disabled={!isEditing}
                  rows={8}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground disabled:bg-muted disabled:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                />
              </div>
            </div>

            {/* Termination Clauses */}
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <Icon name="AlertTriangle" size={20} className="mr-2 text-primary" />
                Termination Clauses
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Termination Conditions
                </label>
                <textarea
                  value={formData?.terminationClauses}
                  onChange={(e) => handleInputChange('terminationClauses', e?.target?.value)}
                  disabled={!isEditing}
                  rows={6}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground disabled:bg-muted disabled:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Legal Summary */}
        <div className="mt-8 bg-card p-6 rounded-lg border border-border">
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Icon name="FileText" size={20} className="mr-2 text-primary" />
            Legal Framework Summary
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <Icon name="Scale" size={24} className="mx-auto text-primary mb-2" />
              <div className="text-sm font-medium text-foreground">Governing Law</div>
              <div className="text-xs text-muted-foreground mt-1">{formData?.governingLaw}</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <Icon name="MapPin" size={24} className="mx-auto text-primary mb-2" />
              <div className="text-sm font-medium text-foreground">Jurisdiction</div>
              <div className="text-xs text-muted-foreground mt-1">{formData?.jurisdiction}</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <Icon name="Users" size={24} className="mx-auto text-primary mb-2" />
              <div className="text-sm font-medium text-foreground">Dispute Resolution</div>
              <div className="text-xs text-muted-foreground mt-1">{formData?.disputeResolution}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JurisdictionTab;